import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { skyProductURL, skyQueryKey } from './skyEndpoints'
import type { LlmUsageDimension } from '../../types/llmUsage'

/**
 * The wire shape of `GET /sky-social-media/:product/spend`
 * (`goapi/pkg/server/sky_spend.go`'s `SkyProductSpend`).
 *
 * TOKENS ONLY, by construction on the server side — there is no `cost` or
 * `currency` field here, on any role, because the handler never has one to
 * send (`stripCost` clears it unconditionally). Do not add one speculatively;
 * that would resurrect the exact leak the handler exists to prevent.
 *
 * NO CAMPAIGN BREAKDOWN. The handler deliberately strips the summary's
 * `ByLabel` (campaign_id) grouping before it ever reaches JSON — a
 * department's campaign membership is Sky-internal commercial information,
 * the same rule the product chooser already follows for its cards (see
 * `sky_spend.go`'s doc comment on `SkyProductSpend` and
 * `TestGetSkyProductSpend_DoesNotEchoCampaignIDs`). `by_feature` and
 * `by_model` are the only breakdowns this response carries.
 */
export interface SkyProductSpend {
  request_count: number
  total_input_tokens: number
  total_output_tokens: number
  total_tokens: number
  total_cached_tokens: number
  total_cache_creation_tokens: number
  total_reasoning_tokens: number
  by_feature: LlmUsageDimension[]
  by_model: LlmUsageDimension[]
}

export interface SkySpendWindow {
  /** Unix ms. Omitted (or 0) means "no lower bound" — the server's own default. */
  startTime?: number
  /** Unix ms. Omitted (or 0) means "no upper bound", i.e. up to now. */
  endTime?: number
}

/**
 * Builds the query params for the spend request.
 *
 * A pure function so the "0 and undefined both mean unbounded" rule — which
 * mirrors `parseSkySpendTime` on the Go side — is testable without a network
 * mock. `undefined` values are omitted rather than sent as `0`: axios drops
 * `undefined` params, and an explicit `0` is indistinguishable from "unbounded"
 * to the server anyway, so there is nothing an explicit zero would buy here.
 */
export function buildSpendParams(window: SkySpendWindow): Record<string, number> {
  const params: Record<string, number> = {}
  if (window.startTime) params.startTime = window.startTime
  if (window.endTime) params.endTime = window.endTime
  return params
}

export interface UseProductSpendResult {
  /** null while loading or on error — never a zeroed object standing in for "no data yet". */
  spend: SkyProductSpend | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * One department's own token spend for a time window, plus its feature/model
 * breakdown. See `SkyProductSpend` above for what this deliberately does NOT
 * carry (cost, campaign identity).
 *
 * Same shape as the rest of `hooks/socialdash/*`: keyed on the product slug so
 * switching departments is a cache miss rather than a flash of the previous
 * department's spend, and gated on `account.user` so a not-yet-authenticated
 * mount fires nothing.
 */
export function useProductSpend(
  product: string,
  window: SkySpendWindow = {},
  enabled = true,
): UseProductSpendResult {
  const account = useAccount()
  const params = buildSpendParams(window)

  const query = useQuery({
    queryKey: skyQueryKey(product, 'spend', params),
    enabled: Boolean(product && account.user && enabled),
    staleTime: 30000,
    retry: 1,
    retryDelay: 1000,
    queryFn: async (): Promise<SkyProductSpend> => {
      try {
        const res = await axios.get<SkyProductSpend>(skyProductURL(product, '/spend'), { params })
        return res.data
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
  })

  return {
    spend: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: () => {
      void query.refetch()
    },
  }
}

export default useProductSpend
